/* eslint-disable no-use-before-define, no-return-assign */
import Promise from 'bluebird';
import cookie from 'cookie-cutter';
import { AuthTypes } from 'enums';
import { SUB_KEY, BAM_PROXY_IFRAME_ID, SESSION_TOKEN_KEY, USER_TRACKING_KEY } from 'constants';
import canUseDOM from 'lib/util/canUseDOM';
import * as perf from 'lib/util/perf';
import {
    getGuest as oneIdGetGuest,
    launchLogin as oneIdLaunchLogin,
    launchReauth as oneIdLaunchReauth,
    forceTokenRefresh as oneIdForceTokenRefresh,
    logout as oneIdLogout,
    on as oneIdOn
} from 'lib/oneId';
import { createSDKWrapper } from 'common/actions/appActions';
import {
    createGetBAMAuthenticationInfoAction,
    createGetBAMSessionTokenAction,
    createGetUserTrackingAction
} from 'common/actions/authenticationActions';
import { hasPageShellSelector } from 'common/selectors/appSelector';
import {
    oneIdLoggedInSelector,
    oneIdEntitlementsSelector
} from 'common/selectors/oneIdSelector';
import { remove } from 'lib/util/localStorage';
import {
    bamSessionTokenSelector,
    superBundleSelector,
    bamSubReadySelector
} from 'common/selectors/authenticationSelectors';
import { hasTDA } from 'lib/auth';

const SWID_RE = /^\{[\w-]+\}/;

export const START_CHECK_SWID_AUTHENTICATION = 'START_CHECK_SWID_AUTHENTICATION';
export const COMPLETE_CHECK_SWID_AUTHENTICATION = 'COMPLETE_CHECK_SWID_AUTHENTICATION';
export const START_CHECK_ONEID_AUTHENTICATION = 'START_CHECK_ONEID_AUTHENTICATION';
export const COMPLETE_CHECK_ONEID_AUTHENTICATION = 'COMPLETE_CHECK_ONEID_AUTHENTICATION';
export const GET_GUEST = 'GET_GUEST';
export const CLEAR_GUEST = 'CLEAR_GUEST';
export const LOGIN_START = 'LOGIN_START';
export const LOGIN_CLOSED = 'LOGIN_CLOSED';

let disneyIDReadyPromise;

export function startSWIDAuthentication() {
    return {
        type: START_CHECK_SWID_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function completeSWIDAuthentication(isAuthenticated, swid) {
    return {
        type: COMPLETE_CHECK_SWID_AUTHENTICATION,
        isAuthenticated,
        swid,
        completeTime: Date.now()
    };
}

export function startOneIDAuthentication() {
    return {
        type: START_CHECK_ONEID_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function createLoginAction(params) {
    return {
        type: LOGIN_START,
        params
    };
}

export function loginClosed() {
    return {
        type: LOGIN_CLOSED
    };
}

export function setOneIdData(data) {
    return {
        type: GET_GUEST,
        data,
        completeTime: Date.now()
    };
}

export function completeOneIDAuthentication(
    isAuthenticated, entitlements, hasTDAEntitlement, hasSuperBundle, bamSubReady = false
) {
    return {
        type: COMPLETE_CHECK_ONEID_AUTHENTICATION,
        isAuthenticated,
        entitlements,
        hasSuperBundle,
        bamSubReady,
        hasTDAEntitlement,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        authType: AuthTypes.BAM,
        completeTime: Date.now()
    };
}

// TODO: Maybe swid should come from some OneId library instead of the cookie
export function checkSWIDAuthentication() {
    return (dispatch) => {
        dispatch(startSWIDAuthentication());
        const swid = cookie.get('SWID');
        const isAuthenticated = SWID_RE.test(swid);

        dispatch(completeSWIDAuthentication(isAuthenticated, swid));
        return Promise.resolve(isAuthenticated);
    };
}

export function getLoggedInStatus() {
    return (dispatch, getState) => {
        const isLoggedIn = oneIdLoggedInSelector(getState());
        return Promise.resolve(isLoggedIn);
    };
}

export function logout() {
    return () => oneIdLogout();
}

export function launchLogin(options, params) {
    return (dispatch) => {
        dispatch(createLoginAction(params));
        return oneIdLaunchLogin(options);
    };
}

export function launchReauth(options, params) {
    return (dispatch) => {
        dispatch(createLoginAction(params));
        return oneIdLaunchReauth(options);
    };
}

export function forceTokenRefresh() {
    return () => oneIdForceTokenRefresh();
}

export function getGuest() {
    return (dispatch) => oneIdGetGuest()
        .then((data) => {
            dispatch(setOneIdData(data));
            return data;
        });
}

export function clearGuest() {
    return {
        type: CLEAR_GUEST,
        directive: 'remove',
        isAuthenticated: false,
        authType: AuthTypes.BAM,
        completeTime: Date.now()
    };
}

let disneyIDReady = false;
let transactionId = 0;

export function checkOneIDAuthentication() {
    return async (dispatch, getState) => {
        perf.markStart('checkOneIDAuthentication');
        dispatch(startOneIDAuthentication());
        const hasPageShell = hasPageShellSelector(getState());
        if (hasPageShell) {
            if (!disneyIDReadyPromise) {
                let data;
                try {
                    data = await dispatch(disneyIDReadyAction());
                    oneIdOn('refresh', async () => {
                        // refresh oneId token in store
                        await dispatch(getGuest());
                        transactionId += 1;
                        // use new oneId token to refresh BAM token by calling SDK Data
                        dispatch(createSDKWrapper(
                            BAM_PROXY_IFRAME_ID,
                            ['geolocation', 'subscriptions', 'sessionToken', 'userTrackingInformation'],
                            transactionId
                        ));
                    });
                    oneIdOn('logout', () => {
                        remove(SUB_KEY);
                        remove(SESSION_TOKEN_KEY);
                        remove(USER_TRACKING_KEY);
                        dispatch(clearGuest());
                        dispatch(createGetBAMAuthenticationInfoAction());
                        dispatch(createGetBAMSessionTokenAction());
                        dispatch(createGetUserTrackingAction({}));
                        dispatch(completeOneIDAuthentication(false, []));
                    });
                } catch (err) {
                    console.error(err);
                    data = await dispatch(getGuest());
                }
                dispatch(setOneIdData(data));
                disneyIDReady = true;
            } else if (!disneyIDReady) {
                return disneyIDReadyPromise;
            } else {
                await dispatch(getGuest());
            }
        } else {
            await dispatch(getGuest());
        }
        const state = getState();
        const isLoggedIn = oneIdLoggedInSelector(state);
        const entitlements = oneIdEntitlementsSelector(state);
        const bamSessionToken = bamSessionTokenSelector(state);
        const hasSuperBundle = superBundleSelector(state);
        const bamSubReady = bamSubReadySelector(state);
        const hasTDAEntitlement = hasTDA(bamSessionToken);
        const isAuthenticated = (entitlements && entitlements.length > 0) || hasTDAEntitlement;
        // TODO: DTC - move this to `createSDKWrapper` action once BAM side can sync user login state with OneID
        dispatch(completeOneIDAuthentication(isAuthenticated, entitlements, hasTDAEntitlement, hasSuperBundle, bamSubReady));
        perf.measureAndTime('checkOneIDAuthentication');
        return {
            isLoggedIn,
            entitlements
        };
    };
}

export function disneyIDReadyAction() {
    return (dispatch) => {
        perf.markStart('disneyIDReadyAction');
        return disneyIDReadyPromise || (disneyIDReadyPromise = new Promise((resolve, reject) => {
            const timeout = 60 * 1000;
            let userCreated = false;
            let userInitialized = false;
            if (!canUseDOM) {
                reject(new Error('memberservices not available server side'));
            } else if (window.jQuery) {
                window.jQuery(document).one('user.created', () => {
                    userCreated = true;
                    if (window.espn && window.espn.memberservices && window.espn.memberservices.ready) {
                        window.espn.memberservices.ready(() => {
                            userInitialized = true;
                            console.log('memberservices.ready'); // eslint-disable-line no-console
                        });
                    }
                    console.log('user.created'); // eslint-disable-line no-console
                });
                window.jQuery(document).one('user.initialized', () => {
                    userInitialized = true;
                    console.log('user.initialized'); // eslint-disable-line no-console
                });
                const userParsedTimer = window.setTimeout(async () => {
                    perf.measureAndTime('disneyIDReadyAction');
                    if (userCreated && userInitialized) {
                        const guest = await dispatch(getGuest());
                        console.log('`user.parsed` timeout, retrieving guest'); // eslint-disable-line no-console
                        perf.measureAndTime('disneyIDReadyAction');
                        resolve(guest);
                        return;
                    }
                    reject(new Error('user.parsed timeout'));
                }, timeout);
                window.jQuery(document).one('user.parsed', (event, guest) => {
                    console.log('user.parsed'); // eslint-disable-line no-console
                    window.clearTimeout(userParsedTimer);
                    perf.measureAndTime('disneyIDReadyAction');
                    resolve(typeof guest !== 'string' ? guest : undefined);
                });
            } else {
                reject(new Error('jQuery is not available with page shell'));
            }
        }));
    };
}



// WEBPACK FOOTER //
// ./app/common/actions/oneIdActions.js